import type { Address } from "viem"
import type { Config } from "wagmi"

import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { IChunkedRpcStrategy } from "@/domains/library/rpc-strategy"

import { poolSwapFee } from "../lens/pool-swap-fee"
import { poolSlot0, type PoolSlot0Result } from "../lens/pool-slot0"

export type PoolSlot0Entry = {
  poolAddress: Address
  result: PoolSlot0Result
}

export type PoolSwapFeeEntry = {
  poolAddress: Address
  fee: number
}

/**
 * Chunked reader for the Manual CL `slot0()` call across many pools. Each pool
 * becomes one RPC call; the `IChunkedRpcStrategy` groups them and routes
 * metrics through the provided `IOrchestratedQuerySender`.
 *
 * Keeps wagmi/viem plumbing in the transport layer; the lens function itself
 * (`poolSlot0`) stays a pure single-pool reader reusable from interactions,
 * hooks, and other transports.
 *
 * Class name stays `ManualCl*` per the unit-006 rule that existing manual-cl
 * filenames/class names keep their names when they move into `thick-v2/` —
 * the folder location is what marks this as the thick-v2 variant, not the
 * symbol itself.
 */
export class ManualClPoolOnChainTransport {
  constructor(
    private readonly config: Config,
    private readonly chunkedRpcStrategy: IChunkedRpcStrategy,
  ) {}

  async loadPoolSlot0s(
    pools: ReadonlyArray<{ address: Address }>,
    chainId: number,
    query: IOrchestratedQuerySender<unknown>,
    defaultChunkSize: number = 25,
  ): Promise<PoolSlot0Entry[]> {
    if (pools.length === 0) return []

    const chunkResults = await this.chunkedRpcStrategy.executeChunked(
      [...pools],
      query,
      defaultChunkSize,
      async (chunk: Array<{ address: Address }>) => {
        // Parallelise within a chunk; the strategy decides chunk-to-chunk
        // ordering and metric capture.
        const entries = await Promise.all(
          chunk.map(async (pool) => ({
            poolAddress: pool.address,
            result: await poolSlot0({
              poolAddress: pool.address,
              chainId,
              config: this.config,
            }),
          })),
        )
        return entries
      },
      query.getRpcMetricsCollector(),
    )

    return chunkResults.flat()
  }

  async loadPoolSwapFees(
    pools: ReadonlyArray<{ address: Address }>,
    chainId: number,
    query: IOrchestratedQuerySender<unknown>,
    defaultChunkSize: number = 25,
  ): Promise<PoolSwapFeeEntry[]> {
    if (pools.length === 0) return []

    const chunkResults = await this.chunkedRpcStrategy.executeChunked(
      [...pools],
      query,
      defaultChunkSize,
      async (chunk: Array<{ address: Address }>) => {
        const entries = await Promise.all(
          chunk.map(async (pool) => {
            try {
              return {
                poolAddress: pool.address,
                fee: await poolSwapFee({
                  poolAddress: pool.address,
                  chainId,
                  config: this.config,
                }),
              }
            } catch {
              if (import.meta.env.DEV) {
                console.debug(`[thick-v2] skipping swap fee for pool ${pool.address}; fee() call failed.`)
              }
              return null
            }
          }),
        )
        return entries.filter((entry): entry is PoolSwapFeeEntry => Boolean(entry))
      },
      query.getRpcMetricsCollector(),
    )

    return chunkResults.flat()
  }
}
